import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import seon from '@seontechnologies/seon-javascript-sdk';
import { UnsavedChangesModal, useOverlayContext, usePrevious } from 'seagull';
import { useStore, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { ServerError, errorTextsPath } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { relationshipModel } from '~/models';
import { requestAccountantAccessModel } from '~/models/request-accountant-access';
import StripeElementsProvider from '~/providers/StripeElementsProvider';
import { selectors } from '~/store/state-selectors';
import { ResponsiveProvider } from '../../contexts';
import { BrandingActionsProvider } from '../BrandingActionsContext';
import { COBStep, getIsAllowedToSIgn, isApproveProposalDisabled, PreviewState } from '../ClientOnboarding.utils';
import { OverrideBranding, useCobBranding } from '../CobBrandingContext';
import { CobConfigProvider } from '../CobConfigContext';
import { useCobStorage } from '../CobStorageContext';
import { ClientOnboardingContent } from './ClientOnboardingContent/ClientOnboardingContent';
import { computeApproveDisabledReason, getReachableInnerSteps, hasAtLeastOneServiceCommitted } from './ClientOnboardingInner.utils';
import { OptionalServicesContext, PackageSelectionContext, PaymentMethodContext, SignAndApproveContext, useOptionalServicesState, usePackageSelectionState, usePaymentMethodSelection, useTermsOfServiceStatement } from './hooks';
import { computeVisitedSteps } from './visitedSteps';
const TERMS_STEP_BY_INDEX = {
    0: COBStep.termsOfService1,
    1: COBStep.termsOfService2,
    2: COBStep.termsOfService3,
    3: COBStep.termsOfService4
};
export const ClientOnboardingInner = (props)=>{
    var _paymentMethodContextValue_selectedPaymentMethod, _paymentMethodContextValue_selectedPaymentMethod1;
    const { relationship, currentStep, previewState, disableMobile, dismissPreviewModal, onPreviewStateChange, setCurrentStep, deviceView, onDeviceViewChange, entityContext } = props;
    const [localPreviewState, setLocalPreviewState] = useState(previewState);
    const [signature, setSignature] = useState(null);
    const [approveAgreementStatus, setApproveAgreementStatus] = useState(null);
    const [postApprovalRelationship, setPostApprovalRelationship] = useState(null);
    const effectiveRelationship = postApprovalRelationship !== null && postApprovalRelationship !== void 0 ? postApprovalRelationship : relationship;
    const { isDirty, revertToInitial } = useCobBranding();
    const overlayContext = useOverlayContext();
    const { showModal } = overlayContext;
    const userId = useStore(selectors.user.id);
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: obTexts } = useLocaleKeys('clientFlow.onboarding');
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    const role = useStore(selectors.user.role);
    const { api } = useStoreActions();
    const { CUSTOM_PAGES } = useFeatureFlags();
    const isPreview = !!previewState;
    const shouldSignStatement = getIsAllowedToSIgn({
        relationship,
        userId,
        role
    });
    const reachableSteps = useMemo(()=>getReachableInnerSteps(relationship, {
            customPagesFFEnabled: !!CUSTOM_PAGES
        }), [
        relationship,
        CUSTOM_PAGES
    ]);
    const getStepIndex = (step)=>reachableSteps.indexOf(step);
    const currentStepIndex = getStepIndex(currentStep);
    const [highestReachedIndex, setHighestReachedIndex] = useState(()=>Math.max(currentStepIndex, 0));
    const { getFromStorage: getFromCobStorage, setToStorage: setToCobStorage, clearStorage: clearCobStorage } = useCobStorage(relationship);
    const [proposalVersionChanged] = useState(()=>{
        if (isPreview) return false;
        const storedVersion = getFromCobStorage('proposal_version');
        const changed = storedVersion != null && storedVersion !== relationship.version;
        if (changed) {
            clearCobStorage();
        }
        setToCobStorage('proposal_version', relationship.version);
        return changed;
    });
    useOnMount(()=>{
        if (isPreview) return;
        if (proposalVersionChanged) {
            setCurrentStep(COBStep.welcome);
            return;
        }
        if (relationshipModel.hasUserSigned(relationship, userId)) {
            setCurrentStep(COBStep.allSet);
        }
    });
    useEffect(()=>{
        if (currentStepIndex > highestReachedIndex) {
            setHighestReachedIndex(currentStepIndex);
        }
    }, [
        currentStepIndex,
        highestReachedIndex
    ]);
    const visitedSteps = useMemo(()=>computeVisitedSteps(reachableSteps, highestReachedIndex, isPreview), [
        reachableSteps,
        highestReachedIndex,
        isPreview
    ]);
    const prevStep = usePrevious(currentStep);
    const directionRef = useRef(1);
    const hasVisitedCoverPageRef = useRef(false);
    const isCoverPage = currentStep === COBStep.welcome;
    const wasCoverPage = prevStep === COBStep.welcome;
    if (prevStep && prevStep !== currentStep && !isCoverPage && !wasCoverPage) {
        directionRef.current = getStepIndex(currentStep) > getStepIndex(prevStep) ? 1 : -1;
    }
    const handleBack = ()=>{
        if (currentStepIndex === 0) {
            setCurrentStep(COBStep.welcome);
        } else {
            setCurrentStep(reachableSteps[currentStepIndex - 1]);
        }
    };
    const handleAdvanceToNextStep = useCallback(()=>{
        if (currentStepIndex < reachableSteps.length - 1) {
            setCurrentStep(reachableSteps[currentStepIndex + 1]);
        }
    }, [
        currentStepIndex,
        reachableSteps,
        setCurrentStep
    ]);
    const { currentStatement, isStatementAccepted, hasUnacceptedStatements, unacceptedStatements, approvedLegalTermsIds, handleStatementChange } = useTermsOfServiceStatement({
        relationship,
        currentStep,
        shouldSignStatement,
        isPreview
    });
    const { hideNextButton, packageSelectionContextValue, selectedBundleId } = usePackageSelectionState({
        relationship,
        currentStep,
        userId,
        handleNext: handleAdvanceToNextStep
    });
    const { optionalServicesContextValue, selectedOptionalServices } = useOptionalServicesState({
        relationship,
        selectedBundleId
    });
    const { isPaymentMethodNextDisabled, paymentMethodContextValue } = usePaymentMethodSelection({
        relationship,
        currentStep,
        isPreview
    });
    const isReviewer = relationshipModel.isReviewer(relationship, userId);
    const hasPaymentMethod = relationship.requirePaymentMethod || relationship.isSample;
    const isAllowedToSign = getIsAllowedToSIgn({
        relationship,
        userId,
        role
    });
    const disabledReason = useMemo(()=>{
        var _paymentMethodContextValue_selectedPaymentMethod;
        return computeApproveDisabledReason({
            currentStep,
            isPreview,
            isReviewer,
            isAllowedToSign,
            hasPaymentMethod,
            hasSelectedFundingSource: !!(paymentMethodContextValue === null || paymentMethodContextValue === void 0 ? void 0 : (_paymentMethodContextValue_selectedPaymentMethod = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContextValue_selectedPaymentMethod === void 0 ? void 0 : _paymentMethodContextValue_selectedPaymentMethod.fundingSourceId),
            hasUnacceptedStatements,
            relationship,
            selectedBundleId: packageSelectionContextValue === null || packageSelectionContextValue === void 0 ? void 0 : packageSelectionContextValue.selectedBundleId,
            selectedOptionalServices
        });
    }, [
        isReviewer,
        isAllowedToSign,
        isPreview,
        hasPaymentMethod,
        hasUnacceptedStatements,
        currentStep,
        relationship,
        packageSelectionContextValue === null || packageSelectionContextValue === void 0 ? void 0 : packageSelectionContextValue.selectedBundleId,
        paymentMethodContextValue === null || paymentMethodContextValue === void 0 ? void 0 : (_paymentMethodContextValue_selectedPaymentMethod = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContextValue_selectedPaymentMethod === void 0 ? void 0 : _paymentMethodContextValue_selectedPaymentMethod.fundingSourceId,
        selectedOptionalServices
    ]);
    const baseDisabled = isApproveProposalDisabled(disabledReason, isPreview, relationship, userId, role);
    const handleAgreementSubmission = useCallback(async (approvalSignature)=>{
        var _paymentMethodContextValue_selectedPaymentMethod, _paymentMethodContextValue_selectedPaymentMethod1;
        let clientContext;
        try {
            clientContext = await seon.getSession();
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    source: 'seon-approval'
                }
            });
        }
        const loaderName = 'lastStep';
        overlayContext.startLoader(loaderName);
        const payload = relationshipModel.isSignatory(relationship, userId) ? _object_spread({
            fundingSourceId: paymentMethodContextValue === null || paymentMethodContextValue === void 0 ? void 0 : (_paymentMethodContextValue_selectedPaymentMethod = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContextValue_selectedPaymentMethod === void 0 ? void 0 : _paymentMethodContextValue_selectedPaymentMethod.fundingSourceId,
            requestAccountantAccessApproved: requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess),
            approvalSignature,
            approvedLegalTermsIds
        }, clientContext ? {
            clientContext
        } : {}) : _object_spread(_object_spread_props(_object_spread({
            fundingSourceId: paymentMethodContextValue === null || paymentMethodContextValue === void 0 ? void 0 : (_paymentMethodContextValue_selectedPaymentMethod1 = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContextValue_selectedPaymentMethod1 === void 0 ? void 0 : _paymentMethodContextValue_selectedPaymentMethod1.fundingSourceId
        }, relationship.serviceBundles && {
            selectedBundleId: packageSelectionContextValue === null || packageSelectionContextValue === void 0 ? void 0 : packageSelectionContextValue.selectedBundleId
        }), {
            selectedOptionalServices,
            requestAccountantAccessApproved: requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess),
            approvalSignature,
            approvedLegalTermsIds
        }), clientContext ? {
            clientContext
        } : {});
        const request = relationshipModel.isSignatory(relationship, userId) ? api.relationship.signatoryApproveV2(relationship, payload) : api.relationship.approveV2(relationship, payload);
        const { success, error } = await handleRequest(request);
        overlayContext.stopLoader(loaderName);
        if (!success) {
            if (error.hasServerError(ServerError.UserAlreadySigned)) {
                overlayContext.showSuccessToast(commonTexts.toasts.youHaveAlreadySignedTheAgreement());
            } else {
                const errorText = error.approveRelationshipErrorText(errorTexts);
                errorText && overlayContext.showErrorToast(errorText);
                throw error;
            }
        }
    }, [
        api,
        relationship,
        userId,
        overlayContext,
        paymentMethodContextValue === null || paymentMethodContextValue === void 0 ? void 0 : (_paymentMethodContextValue_selectedPaymentMethod1 = paymentMethodContextValue.selectedPaymentMethod) === null || _paymentMethodContextValue_selectedPaymentMethod1 === void 0 ? void 0 : _paymentMethodContextValue_selectedPaymentMethod1.fundingSourceId,
        packageSelectionContextValue === null || packageSelectionContextValue === void 0 ? void 0 : packageSelectionContextValue.selectedBundleId,
        selectedOptionalServices,
        approvedLegalTermsIds
    ]);
    const handleApprove = useCallback(async (approvalSignature)=>{
        try {
            setApproveAgreementStatus('loading');
            await handleAgreementSubmission(approvalSignature);
            try {
                const updated = await api.relationship.get(relationship.id, {
                    disable403Handling: true
                });
                setPostApprovalRelationship(updated);
            } catch (unused) {
            // Post-approval access may be restricted for magic-link recipients.
            // Keep the user on the success screen with the pre-approval snapshot.
            }
            setApproveAgreementStatus(null);
            setCurrentStep(COBStep.allSet);
        } catch (unused) {
            setApproveAgreementStatus('error');
        }
    }, [
        api,
        handleAgreementSubmission,
        relationship.id,
        setCurrentStep
    ]);
    const handleNext = useCallback(()=>{
        if (currentStep === COBStep.signAndApprove) {
            if (isPreview) {
                dismissPreviewModal === null || dismissPreviewModal === void 0 ? void 0 : dismissPreviewModal();
                return;
            }
            if (signature) {
                handleApprove(signature);
                return;
            }
        }
        if (currentStepIndex < reachableSteps.length - 1) {
            setCurrentStep(reachableSteps[currentStepIndex + 1]);
        }
    }, [
        currentStep,
        currentStepIndex,
        reachableSteps,
        setCurrentStep,
        signature,
        handleApprove,
        isPreview,
        dismissPreviewModal
    ]);
    const isOnSignAndApprove = currentStep === COBStep.signAndApprove;
    const signAndApproveDisabled = isOnSignAndApprove && (isPreview || !signature || approveAgreementStatus === 'loading' || hasUnacceptedStatements);
    const hasNoServiceCommitted = !hasAtLeastOneServiceCommitted(relationship, packageSelectionContextValue === null || packageSelectionContextValue === void 0 ? void 0 : packageSelectionContextValue.selectedBundleId, selectedOptionalServices);
    const reviewServicesNextDisabled = currentStep === COBStep.reviewServices && hasNoServiceCommitted;
    const combinedIsNextDisabled = isPaymentMethodNextDisabled || signAndApproveDisabled || reviewServicesNextDisabled;
    const effectiveHideNextButton = hideNextButton;
    const navigateToTermsStep = useCallback((index)=>{
        const target = TERMS_STEP_BY_INDEX[index];
        if (target != null) setCurrentStep(target);
    }, [
        setCurrentStep
    ]);
    const signAndApproveContextValue = useMemo(()=>({
            signature,
            setSignature,
            baseDisabled,
            disabledReason,
            unacceptedStatements,
            navigateToTermsStep,
            hasNoServiceCommitted
        }), [
        signature,
        baseDisabled,
        disabledReason,
        unacceptedStatements,
        navigateToTermsStep,
        hasNoServiceCommitted
    ]);
    const toggleBrandSetup = useCallback(async ()=>{
        if (localPreviewState === PreviewState.SetupBrand) {
            if (isDirty) {
                const confirmed = await showModal({
                    biName: 'unsaved_changes_modal',
                    closeOnClickOutside: true,
                    closeOnEscapeKey: true,
                    content: (param)=>{
                        let { dismissModal } = param;
                        return /*#__PURE__*/ _jsx(OverrideBranding, {
                            children: /*#__PURE__*/ _jsx(UnsavedChangesModal, {
                                testId: "client-preview-unsaved-popup",
                                title: obTexts.unsavedBrandChanges(),
                                dismissModal: dismissModal
                            })
                        });
                    }
                });
                if (confirmed) {
                    revertToInitial();
                    setLocalPreviewState(previewState);
                    onPreviewStateChange === null || onPreviewStateChange === void 0 ? void 0 : onPreviewStateChange(previewState);
                }
            } else {
                setLocalPreviewState(previewState);
                onPreviewStateChange === null || onPreviewStateChange === void 0 ? void 0 : onPreviewStateChange(previewState);
            }
        } else {
            setLocalPreviewState(PreviewState.SetupBrand);
            onPreviewStateChange === null || onPreviewStateChange === void 0 ? void 0 : onPreviewStateChange(PreviewState.SetupBrand);
        }
    }, [
        localPreviewState,
        previewState,
        isDirty,
        revertToInitial,
        showModal,
        onPreviewStateChange
    ]);
    return /*#__PURE__*/ _jsx(ResponsiveProvider, {
        disableMobile: disableMobile,
        children: /*#__PURE__*/ _jsx(CobConfigProvider, {
            relationship: effectiveRelationship,
            previewState: localPreviewState,
            originalPreviewState: previewState,
            isPreviewModal: !!previewState,
            entityContext: entityContext,
            children: /*#__PURE__*/ _jsx(BrandingActionsProvider, {
                onToggleBrandSetup: toggleBrandSetup,
                children: /*#__PURE__*/ _jsx(StripeElementsProvider, {
                    children: /*#__PURE__*/ _jsx(PackageSelectionContext.Provider, {
                        value: packageSelectionContextValue,
                        children: /*#__PURE__*/ _jsx(OptionalServicesContext.Provider, {
                            value: optionalServicesContextValue,
                            children: /*#__PURE__*/ _jsx(PaymentMethodContext.Provider, {
                                value: paymentMethodContextValue,
                                children: /*#__PURE__*/ _jsx(SignAndApproveContext.Provider, {
                                    value: signAndApproveContextValue,
                                    children: /*#__PURE__*/ _jsx(ClientOnboardingContent, {
                                        relationship: effectiveRelationship,
                                        currentStep: currentStep,
                                        previewState: previewState,
                                        localPreviewState: localPreviewState,
                                        dismissPreviewModal: dismissPreviewModal,
                                        onToggleBrandSetup: toggleBrandSetup,
                                        showBrandSetupButton: !!previewState,
                                        deviceView: deviceView,
                                        onDeviceViewChange: onDeviceViewChange,
                                        setCurrentStep: setCurrentStep,
                                        visitedSteps: visitedSteps,
                                        directionRef: directionRef,
                                        hasVisitedCoverPageRef: hasVisitedCoverPageRef,
                                        handleBack: handleBack,
                                        handleNext: handleNext,
                                        statement: currentStatement,
                                        isStatementAccepted: isStatementAccepted,
                                        onStatementChange: handleStatementChange,
                                        isNextDisabled: combinedIsNextDisabled,
                                        hideNextButton: effectiveHideNextButton,
                                        hasSignedSuccess: currentStep === COBStep.allSet
                                    })
                                })
                            })
                        })
                    })
                })
            })
        })
    });
};
